import { useEffect, useRef, useState } from "react";
import { searchSymbols } from "../lib/api";

// Strip a leading $, keep letters/digits/.- , uppercase, cap length.
function normalizeFree(raw) {
  return (raw || "")
    .replace(/^\$+/, "")
    .replace(/[^a-zA-Z0-9.\-]/g, "")
    .toUpperCase()
    .slice(0, 10);
}

/**
 * TickerPicker — brutalist symbol search with autocomplete. Local-first
 * backend (SEC US + cached globals, Twelve Data fallback). Selecting a result
 * (or hitting enter on a free-form symbol) sets the active terminal ticker.
 */
export default function TickerPicker({ value, onChange, onAsk }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const data = await searchSymbols(term, ctrl.signal);
        setResults(data.results || []);
        setHi(0);
      } catch {
        /* aborted or network error — leave prior results */
      } finally {
        setLoading(false);
      }
    }, 280);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (sym) => {
    const t = normalizeFree(sym);
    if (!t) return;
    onChange(t);
    setQ("");
    setResults([]);
    setOpen(false);
  };

  const submitFree = () => {
    const t = normalizeFree(q);
    if (t) pick(t);
  };

  const free = normalizeFree(q);

  return (
    <div className="relative" ref={boxRef} data-testid="ticker-picker">
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        <span className="text-[#888] select-none">{">"} ticker:</span>

        {value && (
          <span
            className="inline-flex items-center gap-1.5 border border-black bg-black text-white px-2 py-0.5 font-bold"
            data-testid="ticker-active"
          >
            ${value}
            <button
              type="button"
              onClick={() => onChange(null)}
              className="hover:text-[#ff9e2c] leading-none"
              data-testid="ticker-clear"
              title="clear ticker"
            >
              ×
            </button>
          </span>
        )}

        <span className="inline-flex items-center border border-[#ccc] bg-white">
          <span className="px-1 text-[#888] select-none">$</span>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (results[hi]) pick(results[hi].symbol);
                else submitFree();
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setHi((h) => Math.min(h + 1, results.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHi((h) => Math.max(h - 1, 0));
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            placeholder="search symbol or company"
            className="px-1 py-0.5 font-mono text-[12px] outline-none bg-white w-[190px]"
            data-testid="ticker-input"
            autoComplete="off"
            spellCheck="false"
          />
        </span>

        {value && (
          <button
            type="button"
            onClick={() => onAsk(value)}
            className="tv-link"
            data-testid="ticker-ask"
          >
            ask about ${value} →
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-50 mt-1 w-[340px] max-h-[280px] overflow-auto border-2 border-black bg-white"
          data-testid="ticker-results"
        >
          {loading && results.length === 0 && (
            <div className="px-3 py-2 text-[12px] text-[#888]">searching…</div>
          )}
          {!loading && results.length === 0 && free && (
            <button
              type="button"
              onClick={submitFree}
              className="block w-full text-left px-3 py-2 text-[12px] hover:bg-[#f3f3f3]"
              data-testid="ticker-freeform"
            >
              use <span className="font-mono font-bold">${free}</span> as ticker →
            </button>
          )}
          {results.map((r, i) => (
            <button
              key={`${r.symbol}-${r.exchange}-${r.country}`}
              type="button"
              onMouseEnter={() => setHi(i)}
              onClick={() => pick(r.symbol)}
              className={`w-full text-left px-3 py-2 text-[12px] flex items-baseline justify-between gap-3 ${
                i === hi ? "bg-[#f3f3f3]" : ""
              }`}
              data-testid={`ticker-result-${r.symbol}`}
            >
              <span className="min-w-0 flex items-baseline gap-2">
                <span className="font-mono font-bold whitespace-nowrap">{r.symbol}</span>
                <span className="text-[#666] truncate">{r.name}</span>
              </span>
              <span className="text-[#999] whitespace-nowrap">
                {r.country === "United States" ? r.exchange : r.country}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
